import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import { useUser } from "../auth/useUser";
import { Header } from "../components/Header";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useSpec } from "../hooks/useSpec";
import { getDisplayName } from "../lib/displayName";
import type { components } from "../api/types";

type RunRecord = components["schemas"]["RunRecord"];
type Op = components["schemas"]["Op"];

// Slice 5: stub run-history surface at /products/:pid/changes. Per-run
// detail (full diff, undo affordance) lives in 0010 — when that ships,
// this view becomes the entry point and each row links to a dedicated
// page. For now: vertical list, most-recent first, click a row to
// expand the run's summary + the ops list inline.
//
// To wire: add the route in App.tsx as
//   <Route path="/products/:productId/changes" element={<RequireAuth><Changes /></RequireAuth>} />
export function Changes() {
  const { productId } = useParams<{ productId: string }>();
  const { data: user } = useUser();
  const [expanded, setExpanded] = useState<string | null>(null);

  // The name and idea come from the spec read, the product page's and the
  // map's own (shared cache); this page also refetches it on window focus, as
  // its tree read did.
  const specQuery = useSpec(productId ?? "", { refetchOnWindowFocus: true });

  // Cursor-paginated runs. The orchestrator emits next_cursor (opaque
  // base64url) iff there's more; we use it as the pageParam in
  // useInfiniteQuery so "Load more" becomes a one-line affordance.
  const runsQuery = useInfiniteQuery({
    queryKey: ["product", productId, "runs"],
    queryFn: async ({ pageParam }) => {
      const { data, error } = await apiClient.GET(
        "/api/v1/products/{pid}/runs",
        {
          params: {
            path: { pid: productId! },
            query: pageParam ? { cursor: pageParam } : {},
          },
        },
      );
      if (error) throw error;
      return data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last?.next_cursor || undefined,
    enabled: !!productId,
  });

  const displayName = specQuery.data ? getDisplayName(specQuery.data.meta) : null;
  useDocumentTitle(displayName);

  if (!user) return null;

  const ideaText = specQuery.data?.meta.idea ?? "";
  const namePreview = displayName ? truncate(displayName, 40) : "loading…";

  const breadcrumb = (
    <>
      <span className="text-ink-600">/</span>
      <Link to="/" className="font-mono text-ink-400 hover:text-ink-200">
        ideas
      </Link>
      <span className="text-ink-600">/</span>
      <Link
        to={`/products/${productId}`}
        className="font-mono text-ink-300 hover:text-ink-100 truncate max-w-[28ch]"
        title={ideaText}
      >
        {namePreview}
      </Link>
      <span className="text-ink-600">/</span>
      <span className="font-mono text-ink-300">changes</span>
    </>
  );

  const allRuns: RunRecord[] = (runsQuery.data?.pages ?? []).flatMap(
    (p) => p?.runs ?? [],
  );
  const hasNext = !!runsQuery.hasNextPage;

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} breadcrumb={breadcrumb} />

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-8 py-12">
        {/* Run-history list reads better at a comfortable text width
            (~3xl); outer max-w-6xl keeps the page chrome aligned with
            the rest of the app. */}
        <div className="max-w-3xl">
        <div className="mb-10 animate-fade-in">
          <p className="font-mono text-xs text-ink-400 tracking-widest uppercase mb-2">
            Run history
          </p>
          <h1 className="text-display font-medium text-ink-50 tracking-tightest">
            Changes
          </h1>
          <p className="mt-2 text-sm text-ink-300">
            Every analysis run, most recent first. Click a row to see the
            ops it applied.
          </p>
        </div>

        {runsQuery.isLoading && (
          <p className="text-sm text-ink-400 font-mono">loading runs…</p>
        )}

        {runsQuery.error && (
          <div className="border border-danger-solid/30 bg-danger-solid/10 rounded-md p-4 mb-6 text-sm text-danger-strong">
            Failed to load runs.{" "}
            {String((runsQuery.error as Error).message ?? "")}
          </div>
        )}

        {!runsQuery.isLoading && allRuns.length === 0 && !runsQuery.error && (
          <div className="text-center py-24 border-t border-ink-800">
            <p className="font-mono text-xs text-ink-400 uppercase tracking-widest mb-3">
              empty
            </p>
            <p className="text-ink-200">No runs yet.</p>
            <Link
              to={`/products/${productId}`}
              className="mt-6 inline-block text-sm text-accent hover:text-accent-glow"
            >
              ← back to spec
            </Link>
          </div>
        )}

        {allRuns.length > 0 && (
          <div className="border-t border-ink-800">
            {allRuns.map((run, i) => (
              <RunRow
                key={run.run_id}
                run={run}
                expanded={expanded === run.run_id}
                onToggle={() =>
                  setExpanded(expanded === run.run_id ? null : run.run_id)
                }
                animationDelay={Math.min(i * 30, 240)}
              />
            ))}

            <div className="mt-8 flex items-center justify-between">
              {hasNext ? (
                <button
                  type="button"
                  onClick={() => runsQuery.fetchNextPage()}
                  disabled={runsQuery.isFetchingNextPage}
                  className="font-mono text-xs text-ink-400 hover:text-ink-200 transition-colors disabled:opacity-50"
                >
                  {runsQuery.isFetchingNextPage ? "loading…" : "load more →"}
                </button>
              ) : (
                <p className="font-mono text-xs text-ink-500">
                  end of list · {allRuns.length} run
                  {allRuns.length === 1 ? "" : "s"}
                </p>
              )}
            </div>
          </div>
        )}
        </div>
      </main>
    </div>
  );
}

// ---- Run row --------------------------------------------------------

function RunRow({
  run,
  expanded,
  onToggle,
  animationDelay,
}: {
  run: RunRecord;
  expanded: boolean;
  onToggle: () => void;
  animationDelay: number;
}) {
  const opCount = run.changes?.ops?.length ?? null;
  const summary = run.summary || "—";

  return (
    <div
      className="border-b border-ink-800 animate-slide-up"
      style={{
        animationDelay: `${animationDelay}ms`,
        animationFillMode: "backwards",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        className="group w-full text-left px-2 py-5 hover:bg-ink-900/40 transition-colors"
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-1.5">
              {timeAgo(run.started_at)}
              <span className="text-ink-600 mx-1.5">·</span>
              <span className="text-ink-500">{run.run_id}</span>
              <span className="text-ink-600 mx-1.5">·</span>
              <span className="text-ink-500">{run.type}</span>
            </p>
            <p className="text-[14px] text-ink-200 group-hover:text-ink-100 transition-colors truncate">
              {summary}
            </p>
          </div>

          <div className="flex items-center gap-5 text-[11px] font-mono shrink-0">
            <span className="text-ink-400">
              {opCount === null ? (
                <span className="text-ink-500">— ops</span>
              ) : (
                <>
                  <span className="text-ink-200">{opCount}</span>{" "}
                  <span className="text-ink-500">
                    op{opCount === 1 ? "" : "s"}
                  </span>
                </>
              )}
            </span>
            <StatusBadge status={run.status} />
            <span className="text-ink-500 group-hover:text-ink-200 transition-colors">
              {expanded ? "↑" : "↓"}
            </span>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-2 pb-6 pt-1 animate-fade-in">
          {run.summary && (
            <div className="mb-5 border-l-2 border-ink-800 pl-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-1">
                Summary
              </p>
              <p className="text-[13px] text-ink-300 leading-relaxed whitespace-pre-wrap">
                {run.summary}
              </p>
            </div>
          )}

          {opCount && opCount > 0 ? (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-2">
                Ops · {opCount}
              </p>
              <ul className="space-y-1.5">
                {(run.changes?.ops ?? []).map((op, i) => (
                  <OpRow key={i} op={op} />
                ))}
              </ul>
            </div>
          ) : (
            <p className="font-mono text-[11px] text-ink-500">
              No ops recorded.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function OpRow({ op }: { op: Op }) {
  const desc = describeOp(op);
  return (
    <li className="flex items-baseline gap-3 text-[12px]">
      <span className="font-mono text-[10px] uppercase tracking-widest text-ink-500 min-w-[140px] shrink-0">
        {op.kind.replaceAll("_", " ")}
      </span>
      <span className="text-ink-300 break-words">{desc}</span>
    </li>
  );
}

// Exported for its own vitest (frontend/src/__tests__/describeOp.test.ts). It is a
// pure Op -> string function and the only prose the Ops list renders, so pinning it
// through the smallest exported surface means exporting it rather than rendering the
// whole Changes view (queries, router, auth) to read one row's text.
export function describeOp(op: Op): string {
  switch (op.kind) {
    case "node_created":
      return op.node_id
        ? `created ${op.node_id}${op.node_shape?.title ? ` · ${op.node_shape.title}` : ""}`
        : "created node";
    case "node_deleted":
      return op.node_id ? `deleted ${op.node_id}` : "deleted node";
    case "status_transitioned":
      return `${op.node_id ?? ""}: ${stringify(op.from)} → ${stringify(op.to)}`;
    case "field_modified":
      return `${op.node_id ?? ""} · ${op.field ?? "field"}`;
    case "ref_added":
      return `${op.node_id ?? ""} → ${op.ref?.to ?? ""}${op.ref?.rel ? ` (${op.ref.rel})` : ""}`;
    case "ref_removed":
      return `${op.node_id ?? ""} ✕ ${op.ref?.to ?? ""}${op.ref?.rel ? ` (${op.ref.rel})` : ""}`;
    case "tree_field_set":
      return `meta.${op.field ?? "?"}`;
    case "tree_meta_json_set":
      // A SHARED kind: every tree.Meta map writes through it, so the FIELD PATH is
      // what names the change and only one path has founder vocabulary today. The
      // resolved-pair record is the one the founder has a reason to look for - it
      // is why a re-check they already ruled on stopped being asked.
      //
      // THE TRADE for every OTHER meta path, stated because adding this case
      // CHANGES what they render: before it existed they fell through to the
      // default arm and showed op.reason, which is NOT blank - it is the emitter's
      // prose, and every map routed through the shared struct-meta op builders
      // carries the SAME engine-internal sentence, "V2 bridge emitted struct meta
      // write" (analyze/bridge_apply.go's buildMetaJSONOp, decide/settledness_
      // cache.go's decideBuildMetaJSONOp). The raw field path is the more
      // identifying of the two: it names WHICH map moved, which the shared sentence
      // never did. Reversible in one line if a reason prose ever earns the row back.
      return op.field === "meta.authority_resolved_pairs"
        ? "recorded your ruling's coverage"
        : (op.field ?? "meta.?");
    default:
      return op.reason ?? "";
  }
}

function stringify(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function StatusBadge({ status }: { status: string }) {
  // Map known terminal statuses to colors; unknown statuses fall through
  // to mute. The orchestrator emits a free-form status string so we can't
  // exhaustively switch — keep it best-effort.
  const lower = status.toLowerCase();
  let cls = "text-ink-400 border-ink-700";
  if (lower === "complete" || lower === "completed" || lower === "ok") {
    cls = "text-ok border-ok-solid/30";
  } else if (lower === "error" || lower === "failed") {
    cls = "text-danger border-danger-solid/40";
  } else if (lower === "running" || lower === "in_progress") {
    cls = "text-accent border-accent/40";
  } else if (lower === "no_op") {
    cls = "text-ink-500 border-ink-800";
  }
  return (
    <span
      className={`font-mono text-[10px] uppercase tracking-widest border px-1.5 py-0.5 rounded-sm ${cls}`}
    >
      {status}
    </span>
  );
}

// ---- Helpers --------------------------------------------------------

function timeAgo(iso: string | undefined): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return "—";
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  const week = 7 * day;
  if (diff < minute) return "just now";
  if (diff < hour) {
    const m = Math.floor(diff / minute);
    return `${m} minute${m === 1 ? "" : "s"} ago`;
  }
  if (diff < day) {
    const h = Math.floor(diff / hour);
    return `${h} hour${h === 1 ? "" : "s"} ago`;
  }
  if (diff < week) {
    const d = Math.floor(diff / day);
    return `${d} day${d === 1 ? "" : "s"} ago`;
  }
  const w = Math.floor(diff / week);
  return `${w} week${w === 1 ? "" : "s"} ago`;
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + "…";
}
